export type BorderRadiusLonghand = {
  borderTopLeftRadius: string;
  borderTopRightRadius: string;
  borderBottomRightRadius: string;
  borderBottomLeftRadius: string;
};

const borderRadiusValues = (
  topLeft: string,
  topRight: string = topLeft,
  bottomRight: string = topLeft,
  bottomLeft: string = topRight,
): BorderRadiusLonghand => ({
  borderTopLeftRadius: topLeft,
  borderTopRightRadius: topRight,
  borderBottomRightRadius: bottomRight,
  borderBottomLeftRadius: bottomLeft,
});
/**
 * Converts a CSS border radius style into an object containing longhand values for all 4 edges.
 * Supports global values ("inherit", "none", etc), short-hand values and also different values for horizontal and vertical border radius.
 * https://developer.mozilla.org/en-US/docs/Web/CSS/border-radius
 *
 * @param borderRadiusCSS A valid CSS style string for border radius.
 * @returns Object containing a computed value for each corner.
 */
export const parseBorderRadius = (borderRadiusCSS: string): BorderRadiusLonghand => {
  // different values for horizontal and vertical radius
  if (borderRadiusCSS.includes('/')) {
    const [horizontalR, verticalR] = borderRadiusCSS.split('/');

    // get values for horizontal and vertical radius (up to 4 allowed)
    const [hor1, hor2, hor3, hor4] = horizontalR.split(' ').filter(Boolean);
    const [ver1, ver2, ver3, ver4] = verticalR.split(' ').filter(Boolean);

    // if at least one exists, calculate value for each corner
    if (hor1 && ver1) {
      const valsHor = borderRadiusValues(hor1, hor2, hor3, hor4);
      const valsVert = borderRadiusValues(ver1, ver2, ver3, ver4);

      // combine both values with a space and return object
      return {
        borderTopLeftRadius: `${valsHor.borderTopLeftRadius} ${valsVert.borderTopLeftRadius}`,
        borderTopRightRadius: `${valsHor.borderTopRightRadius} ${valsVert.borderTopRightRadius}`,
        borderBottomRightRadius: `${valsHor.borderBottomRightRadius} ${valsVert.borderBottomRightRadius}`,
        borderBottomLeftRadius: `${valsHor.borderBottomLeftRadius} ${valsVert.borderBottomLeftRadius}`,
      };
    }

    return borderRadiusValues('');
  }

  // we have a simple css string with up to 4 values
  const [v1, v2, v3, v4] = borderRadiusCSS.split(' ').filter(Boolean);

  // if at lest one exists, return object with computed values
  if (v1) {
    return borderRadiusValues(v1, v2, v3, v4);
  }

  return borderRadiusValues('');
};
